import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "./Menu";
import { useNavigate, useParams } from "react-router-dom";
import useApiUrls from "../useApiUrls";

const SendJsonPackage = () => {
  const { merchant, pos } = useParams();
  const [merchantname, setMerchantname] = useState(merchant || "all");
  const [posid, setPosid] = useState(pos || "0"); // Default to "0" for All
  const [actionname, setActionname] = useState(""); // New state for action name
  const [message, setMessage] = useState(""); // New state for message input
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [merchantList, setMerchantList] = useState([]);
  const [posList, setPosList] = useState([]);
  const [actionList, setActionList] = useState([]); // New state for action list
  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  // Fetch action names when dropdown is focused
  const fetchActions = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    const getDistinctUrl = getUrlByOperation("ActionOperationGetDistinctName");
    if (!getDistinctUrl) return;

    try {
      const response = await axios.get(
        getDistinctUrl,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const actions = response.data.data.map((item) => ({
          id: item.id,
          value: item.value
        }));
        setActionList(actions);
      }
    } catch (error) {
      console.error("Error fetching action list:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Fetch merchants when dropdown is focused - now includes actionname parameter
  const fetchMerchants = async () => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    try {
      // Construct URL with actionname parameter if actionname is selected
      let url = "https://api.dqr-display-tms.bonrix.in/api/POSOperation/GetDistinct/merchantid";
      const params = new URLSearchParams();
      
      if (actionname && actionname.trim() !== "") {
        params.append("actionname", actionname);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          accept: "text/plain",
        },
      });

      if (response.data && Array.isArray(response.data.data)) {
        const merchants = response.data.data.map((item) => item.merchantid);
        setMerchantList(merchants);
      }
    } catch (error) {
      console.error("Error fetching merchant list:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Fetch POS IDs based on the selected merchant
  const fetchPosByMerchant = async (merchantId) => {
    const token = getCookie("token");
    const userid = getCookie("userid");

    try {
      const response = await axios.get(
        `https://api.dqr-display-tms.bonrix.in/api/POSOperation/GetDistinct/specificmerchantid/${merchantId}`,
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const posIds = response.data.data.map((item) => ({
          id: item.id,
          value: item.value,
        }));
        setPosList(posIds);
      } else {
        setPosList([]);
      }
    } catch (error) {
      console.error("Error fetching POS list:", error);
    }
  };

  // Function to handle the API call with new GeneralAll endpoint
  const handleSendMessage = async () => {
    setLoading(true);
    const token = getCookie("token");
    const userid = getCookie("userid");

    const apiUrl = "https://api.dqr-display-tms.bonrix.in/api/SendToDevice/GeneralAll";
    const requestBody = {
      merchantname: merchantname,
      posid: parseInt(posid) || 0,
      actionname: actionname || "string",
      withaction: true,
      msg: message,
    };

    try {
      const response = await axios.post(apiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
          accept: "text/plain",
        },
      });

      if (response.data && response.data.sendresult) {
        setResponseData(response.data.sendresult);
        setErrorMessage("");
      } else {
        setResponseData(null);
        setErrorMessage(response.data.errormsg || "No matching records found");
      }

      setLoading(false);
    } catch (error) {
      console.error("Error sending message:", error);
      setResponseData(null);
      setErrorMessage("Error occurred while sending message");
      setLoading(false);
    }
  };

  const handleActionChange = async (actionVal) => {
    setActionname(actionVal);
    if (!actionVal) {
      setMessage("");
      return;
    }

    const selectedObj = actionList.find(act => act.value === actionVal);
    if (!selectedObj) return;

    setMessage("Loading configuration JSON...");

    const token = getCookie("token");
    const userid = getCookie("userid");

    const getUrl = getUrlByOperation("ActionOperationGet");
    if (!getUrl) {
      setMessage("");
      return;
    }

    try {
      const response = await axios.post(
        getUrl,
        {
          id: selectedObj.id,
          name: selectedObj.value
        },
        {
          headers: {
            "accept": "text/plain",
            "UserId": userid,
            "X-Authorization": token,
            "Content-Type": "application/json-patch+json"
          }
        }
      );

      if (response.data && response.data.isresponse && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setMessage(response.data.data[0].json || "");
      } else {
        setMessage("");
      }
    } catch (error) {
      console.error("Error fetching action JSON:", error);
      setMessage("");
    }
  };

  // Effect to refresh merchant list when action name changes
  useEffect(() => {
    if (actionname) {
      setMerchantname("all"); // Reset merchant selection
      setPosid("0"); // Reset POS selection
      setPosList([]); // Clear POS list
      fetchMerchants(); // Fetch merchants with new action name
    }
  }, [actionname]);

  return (
    <>
      <Menu />
      {/* Modal for sending message */}
      <div className="content-wrapper" style={{ padding: "20px" }}>
        <div
          style={{
            backgroundColor: "var(--surface)",
            padding: "2rem",
            borderRadius: "8px",
            width: "400px",
            margin: "auto",
          }}
        >
          <h4>Send Row Json</h4>
          
          

          <div className="form-group">
            <label>Merchant Name</label>
            {merchant ? (
              <select className="form-control" value={merchantname} disabled style={{ backgroundColor: 'var(--surface2)', color: 'var(--text-muted)' }}>
                <option value={merchant}>{merchant}</option>
              </select>
            ) : (
              <select
                className="form-control"
                value={merchantname}
                onChange={(e) => {
                  const selectedMerchant = e.target.value;
                  setMerchantname(selectedMerchant);
                  setPosid("0"); // Reset POS ID to "All" when merchant changes
                  if (selectedMerchant && selectedMerchant !== "all") {
                    fetchPosByMerchant(selectedMerchant);
                  } else {
                    setPosList([]);
                  }
                }}
                onFocus={fetchMerchants}
                style={{ backgroundColor: "var(--surface)", color: "var(--text)" }}
              >
                <option value="all">All</option>
                {merchantList.map((merchant, index) => (
                  <option key={index} value={merchant}>
                    {merchant}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>POS ID</label>
            {pos ? (
              <select className="form-control" value={posid} disabled style={{ backgroundColor: 'var(--surface2)', color: 'var(--text-muted)' }}>
                <option value={pos}>{pos}</option>
              </select>
            ) : (
              <select
                className="form-control"
                value={posid}
                onChange={(e) => setPosid(e.target.value)}
                disabled={merchantname === "all"} // Disable if "All" merchants is selected
                style={{ backgroundColor: "var(--surface)", color: "var(--text)" }}
              >
                <option value="0">All</option>
                {posList.map((pos, index) => (
                  <option key={index} value={pos.id}>
                    {pos.value}
                  </option>
                ))}
              </select>
            )}
          </div>
          {/* Action Name Dropdown */}
          <div className="form-group">
            <label>Action Name</label>
            <select
              className="form-control"
              value={actionname}
              onChange={(e) => handleActionChange(e.target.value)}
              onFocus={fetchActions}
              style={{ backgroundColor: "var(--surface)", color: "var(--text)" }}
            >
              <option value="">Select Action</option>
              {actionList.map((action) => (
                <option key={action.id} value={action.value}>
                  {action.value}
                </option>
              ))}
            </select>
            
          </div>
          
          <div className="form-group">
            <label>JSON</label>
            <textarea
              className="form-control"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="{'message' 'Hello World!'}"
              rows="3"
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "1rem",
            }}
          >
            <button
              className="btn btn-primary"
              onClick={handleSendMessage}
              disabled={loading || !message.trim()}
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="row" style={{ marginTop: "20px" }}>
              <div className="col-12">
                <div className="card">
                  <div className="card-header"></div>
                  <div className="card-body table-responsive p-0">
                    <table className="table table-hover text-nowrap">
                      <thead>
                        <tr>
                          <th>Merchant Name</th>
                          <th>POS Name</th>
                          <th>Response Status</th>
                          <th>Error Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {responseData ? (
                          responseData.length > 0 ? (
                            responseData.map((result, index) => (
                              <tr key={index}>
                                <td>{result.merchantname}</td>
                                <td>{result.posname}</td>
                                <td>{result.responsestatus}</td>
                                <td>{result.errormsg}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" style={{ textAlign: "center" }}>
                                No data available yet
                              </td>
                            </tr>
                          )
                        ) : (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center" }}>
                              {errorMessage || "No matching records found"}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SendJsonPackage;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}